import React, { useEffect } from 'react'
import SVGInline from '@/v2/components/common/SVGInline'
import Tab from '@/v2/components/common/Tab'
import CalendarIcon from '#/assets/images/v2/icons/calendar.svg?raw'
import MessagesIcon from '#/assets/images/v2/icons/messages.svg?raw'
import { useNotification } from '@/v2/hooks/messages/useNotification'
import RTNotifications from '@/v2/hooks/subscriptions/messages/RTNotifications'
import PatientTabs from './PatientTabs'
import UnconfirmedTab from './UnconfirmedTab'
import { TabWrapper, Icon, Badge } from './styles'
import LibraryAddCheckIcon from '@mui/icons-material/LibraryAddCheck'
import DashboardIcon from '@mui/icons-material/Dashboard'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import { useUser } from '@/v2/hooks/useUser'

const Tabs = () => {
  const { notification = {} } = useNotification()
  const { user } = useUser()
  // Two different notions of admin: `user.admin` is the legacy global column,
  // while clinic-level permissions live in `user.roles`. Billing keys off the
  // clinic role to match the server's require_admin!.
  const isAdmin = user?.admin || false
  const isClinicAdmin = user?.roles?.includes('admin') || false
  const hasDashboardAccess = isAdmin || user?.roles?.includes('dashboard')
  const { chat, tasks } = notification
  const { messagesUnreadCount = 0, conversationsUnreadCount = 0 } = chat || {}
  const { uncompletedCount } = tasks || {}

  useEffect(() => {
    // Update page title with unread messages count
    const pageTitle = document.title.replace(/^\(\d+\)\s/, '')
    document.title =
      messagesUnreadCount > 0
        ? `(${messagesUnreadCount}) ${pageTitle}`
        : pageTitle
  }, [messagesUnreadCount])

  const handleRequestPermission = () => {
    const isSupported = () =>
      'Notification' in window &&
      'serviceWorker' in navigator &&
      'PushManager' in window
    if (isSupported()) {
      const hasPermission = Notification.permission === 'granted'
      hasPermission && Notification.requestPermission()
    }
  }

  return (
    <>
      <RTNotifications />
      <TabWrapper>
        <Tab title="Calendar" href="/v2/calendar/day" justify="center">
          <Icon>
            <SVGInline svg={CalendarIcon} />
          </Icon>
        </Tab>
        <Tab
          id="messages-button"
          title="Messages"
          href="/v2/messages"
          justify="center"
          onClick={handleRequestPermission}
        >
          {messagesUnreadCount > 0 || conversationsUnreadCount > 0 ? (
            <Badge
              badgeContent={messagesUnreadCount || ''}
              color="secondary"
              variant={messagesUnreadCount === 0 ? 'dot' : 'standard'}
            >
              <Icon>
                <SVGInline svg={MessagesIcon} />
              </Icon>
            </Badge>
          ) : (
            <Icon>
              <SVGInline svg={MessagesIcon} />
            </Icon>
          )}
        </Tab>
        <Tab title="Tasks" href="/v2/tasks" justify="center">
          {uncompletedCount > 0 ? (
            <Badge
              badgeContent={uncompletedCount || ''}
              color="secondary"
              variant={uncompletedCount === 0 ? 'dot' : 'standard'}
            >
              <Icon>
                <LibraryAddCheckIcon />
              </Icon>
            </Badge>
          ) : (
            <Icon>
              <LibraryAddCheckIcon />
            </Icon>
          )}
        </Tab>
        <UnconfirmedTab />
        {hasDashboardAccess && (
          <Tab
            id="dashboard-button"
            title="Dashboard"
            href="/v2/dashboard"
            justify="center"
          >
            <Icon>
              <DashboardIcon />
            </Icon>
          </Tab>
        )}
        {isClinicAdmin && (
          <Tab
            id="billing-dashboard-button"
            title="Billing"
            href="/v2/billing-dashboard"
            justify="center"
          >
            <Icon>
              <ReceiptLongIcon />
            </Icon>
          </Tab>
        )}
        <PatientTabs />
      </TabWrapper>
    </>
  )
}

export default Tabs
